// Generated types for VersioningCommit
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { VersioningRowReadAPI, VersioningRowWriteAPI } from './versioningrow';
import { VersioningRowReadAPISchemaObject, VersioningRowWriteAPISchemaObject } from './versioningrow';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface VersioningCommitReadAPI extends Omit<VersioningRowReadAPI, 'created_at'> {
  /** Commit timestamp (Neo4j clock). */
  created_at?: number | null;
  /** The island root's id. */
  island_id: string;
  /** Branch name. */
  branch?: string;
  /** Parent commit (history chain). */
  parent_commit_id?: string | null;
  /** Second parent of a MERGE commit — the merged-in branch's head, as provenance: since decision a the commit carries copies of the branch's ops and the fold walks one line. Server-set by the merge; a request cannot name one. */
  merge_parent_commit_id?: string | null;
  /** Committing actor (server-set at seal). */
  author?: string;
  /** Commit message. */
  message?: string;
  /** Monotonic per-branch sequence (server-set at seal). */
  seq?: number;
  /** Number of ops stamped (server-set at seal). */
  op_count?: number;
  /** The client's id for THIS commit attempt (a uuid4): the commit's progress is sent to the committing user's own sockets under it (`commit_progress`), and after a lost answer the client finds its commit by it (`commit-by-progress`, the caller's own commits only). Anything that is not a uuid is not kept. */
  progress_id?: string | null;
  /** Where this commit's ops came from, when not the branch's own lane: the overlay it was committed from (`wip/<user>…`, `proposal/<id>`). SEMANTICALLY an overlay's commit is made on the overlay and merged into its branch; the code seals it directly on the branch, and this keeps the provenance that shortcut would drop. Server-set. */
  source_lane?: string | null;
  /** JSON list of constraint violations found in the sealed state (warn only; server-set). */
  constraint_warnings?: string | null;
  /** Model-schema fingerprint at commit time (64 risk 19 — upcasting). */
  schema_version?: string | null;
  /** JSON causal stamps — e.g. observed_island_heads (62 §8 rewind). */
  stamps?: string | null;
  /** The nearest ancestor commit on the parent line with a snapshot (server-set). */
  snapshot_base?: string | null;
  /** Ops committed after the snapshot base up to and including this commit; null = no single line back to a snapshot (a merge, or after one). Server-set. */
  ops_since_snapshot?: number | null;
  /** Whether a snapshot of the island at this commit is stored (server-set). */
  has_snapshot?: boolean;
  /** The island root's subtree hash at this commit, stamped by the snapshot writer from the index it wrote (the hash a pin of this commit records). Server-set. */
  root_hash?: string | null;
  /** The digest epoch `root_hash` was computed under; used only while current. Server-set. */
  root_hash_epoch?: string | null;
  /** The snapshot writer skipped this commit for a newer one on its line (set 53: when it falls behind it snapshots the newest). Server-set. */
  snapshot_skipped?: boolean;
  /** Why the snapshot writer skipped this commit, when not simply for a newer one on its line — e.g. the island was over the writer's size budget. Server-set. */
  snapshot_skip_reason?: string | null;
}

export interface VersioningCommitWriteAPI extends VersioningRowWriteAPI {
  /** Commit timestamp (Neo4j clock). */
  created_at?: number | null;
  /** The island root's id. */
  island_id: string;
  /** Branch name. */
  branch?: string;
  /** Parent commit (history chain). */
  parent_commit_id?: string | null;
  /** Committing actor (server-set at seal). */
  author?: string;
  /** Commit message. */
  message?: string;
  /** Monotonic per-branch sequence (server-set at seal). */
  seq?: number;
  /** Number of ops stamped (server-set at seal). */
  op_count?: number;
  /** COMMAND INPUT: restrict the commit to these working ops (their dependency closure is committed — a partial commit). Consumed by the seal; null = commit everything staged on the branch. */
  requested_op_ids?: string[] | null;
  /** COMMAND INPUT, committing an OVERLAY: the base commit the committer was LOOKING AT. An automatic baseline can re-write a change while the commit dialog is open; if the overlay's base is no longer this, the commit is refused — commit exactly what was shown, or look again. Null = no check. Consumed by the seal. */
  expected_base_commit_id?: string | null;
  /** COMMAND INPUT, committing an OVERLAY: the `review_token` /diff gave for what the committer reviewed. If the overlay changed since — its base or its own ops — the commit is refused `changed_since_review` (review it again). Null = no check. Consumed by the seal. */
  review_token?: string | null;
  /** The client's id for THIS commit attempt (a uuid4): the commit's progress is sent to the committing user's own sockets under it (`commit_progress`), and after a lost answer the client finds its commit by it (`commit-by-progress`, the caller's own commits only). Anything that is not a uuid is not kept. */
  progress_id?: string | null;
  /** Model-schema fingerprint at commit time (64 risk 19 — upcasting). */
  schema_version?: string | null;
  /** JSON causal stamps — e.g. observed_island_heads (62 §8 rewind). */
  stamps?: string | null;
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const VersioningCommitReadAPISchema: z.ZodType<VersioningCommitReadAPI> = VersioningRowReadAPISchemaObject.omit({ 'created_at': true }).extend({
  created_at: z.number().nullable().optional(),
  island_id: z.string(),
  branch: z.string().optional(),
  parent_commit_id: z.string().nullable().optional(),
  merge_parent_commit_id: z.string().nullable().optional(),
  author: z.string().optional(),
  message: z.string().optional(),
  seq: z.number().optional(),
  op_count: z.number().optional(),
  progress_id: z.string().nullable().optional(),
  source_lane: z.string().nullable().optional(),
  constraint_warnings: z.string().nullable().optional(),
  schema_version: z.string().nullable().optional(),
  stamps: z.string().nullable().optional(),
  snapshot_base: z.string().nullable().optional(),
  ops_since_snapshot: z.number().nullable().optional(),
  has_snapshot: z.boolean().optional(),
  root_hash: z.string().nullable().optional(),
  root_hash_epoch: z.string().nullable().optional(),
  snapshot_skipped: z.boolean().optional(),
  snapshot_skip_reason: z.string().nullable().optional(),
}).passthrough();

export const VersioningCommitWriteAPISchema: z.ZodType<VersioningCommitWriteAPI> = VersioningRowWriteAPISchemaObject.extend({
  created_at: z.number().nullable().optional(),
  island_id: z.string(),
  branch: z.string().optional(),
  parent_commit_id: z.string().nullable().optional(),
  author: z.string().optional(),
  message: z.string().optional(),
  seq: z.number().optional(),
  op_count: z.number().optional(),
  requested_op_ids: z.array(z.string()).nullable().optional(),
  expected_base_commit_id: z.string().nullable().optional(),
  review_token: z.string().nullable().optional(),
  progress_id: z.string().nullable().optional(),
  schema_version: z.string().nullable().optional(),
  stamps: z.string().nullable().optional(),
}).passthrough();
